import { Promotion } from '@/@type/promotion';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { hexToRGBA } from '@/utils/rgb';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoadingButton from '@mui/material/Button';
import { Box, Breadcrumbs, Button, Grid, Link, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { createSteps, Step, StepLabel } from './steps';

export interface canMoveProps {
    message: string;
    canMove: boolean;
}

interface PromoStepperHookReturn {
    currentStep: StepLabel;
    setCanMoveFunc: Dispatch<SetStateAction<() => Promise<canMoveProps>>>;
    BreadcrumbsComponent: () => JSX.Element;
    MoveToStepComponent: () => JSX.Element;
}

interface props {
    isSubmitting: boolean;
    action: (isPublished: boolean) => void;
    promotion: Promotion;
}

export const usePromoStepper = (props: props): PromoStepperHookReturn => {
    const { isSubmitting, action, promotion } = props;
    const { resultingPromotions, isOffer = false, isLoyalty = false } = promotion;
    const [steps] = useState<Step[]>(createSteps(isOffer, isLoyalty, !!resultingPromotions));
    const [currentStep, setCurrentStep] = useState(steps[0].label);
    const currentIndex = steps.findIndex((step) => step.label === currentStep);
    const maxLength = steps.length;
    // first step is always activated
    const [activatedSteps, setActivatedSteps] = useState(Array.from({ length: maxLength }, (_, index) => index === 0));
    const { showSnackbar } = useSnackbarHelper();
    const btnBackgroundColor = isOffer ? '#DCC179' : '';
    const btnHoverBackgroundColor = isOffer ? '#DCC179' : '';
    const btnShadowColor = isOffer ? '#DCC179' : '';
    const btnTextColor = isOffer ? '#D0AC4B' : '';
    const activateStep = (index: number): void => {
        // get copy
        const newActivatedSteps = [...activatedSteps];
        // activate the step
        newActivatedSteps[index] = true;
        setActivatedSteps(newActivatedSteps);
    };
    const [canMoveFunc, setCanMoveFunc] = useState<() => Promise<canMoveProps>>(async () => ({
        message: '',
        canMove: true
    }));

    const next = async (): Promise<void> => {
        const { message, canMove } = await canMoveFunc();
        if (!canMove) {
            showSnackbar(message, 'error');
            return;
        }
        const currentIndex = steps.findIndex((step) => step.label === currentStep);
        if (currentIndex < maxLength - 1) {
            const nextStep = steps[currentIndex + 1].label;
            activateStep(currentIndex + 1);
            setCurrentStep(nextStep);
        }
    };

    const back = (): void => {
        const currentIndex = steps.findIndex((step) => step.label === currentStep);
        if (currentIndex > 0) {
            const prevStep = steps[currentIndex - 1].label;
            setCurrentStep(prevStep);
        }
    };

    const MoveToStepComponent = (): JSX.Element => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
            {currentIndex > 0 && (
                <Button
                    sx={{
                        width: 100,
                        borderColor: btnBackgroundColor,
                        color: btnTextColor,
                        '&:hover': {
                            backgroundColor: hexToRGBA(btnBackgroundColor, 0.2),
                            borderColor: btnBackgroundColor,
                            color: btnTextColor
                        }
                    }}
                    variant="outlined"
                    onClick={back}
                >
                    Back
                </Button>
            )}

            <div style={{ flexGrow: 1 }}></div>

            {currentIndex < steps.length - 1 && (
                <Button
                    sx={{
                        width: 100,
                        backgroundColor: btnBackgroundColor,
                        '&:hover': {
                            backgroundColor: btnHoverBackgroundColor
                        },
                        boxShadow: `0px 4px 8px 0px ${btnShadowColor}`
                    }}
                    variant="contained"
                    onClick={next}
                >
                    Next
                </Button>
            )}
            {currentIndex === steps.length - 1 && (
                <>
                    <LoadingButton
                        sx={{
                            width: 100,
                            mx: 1,
                            borderColor: btnBackgroundColor,
                            color: btnTextColor,
                            '&:hover': {
                                backgroundColor: hexToRGBA(btnBackgroundColor, 0.2),
                                borderColor: btnBackgroundColor,
                                color: btnTextColor
                            }
                        }}
                        variant="outlined"
                        onClick={() => {
                            action(false);
                        }}
                        loading={isSubmitting}
                    >
                        Draft
                    </LoadingButton>
                    <LoadingButton
                        sx={{
                            width: 100,
                            mx: 1,
                            backgroundColor: btnBackgroundColor,
                            '&:hover': {
                                backgroundColor: btnHoverBackgroundColor
                            },
                            boxShadow: `0px 4px 8px 0px ${btnShadowColor}`
                        }}
                        variant="contained"
                        onClick={() => {
                            action(true);
                        }}
                        loading={isSubmitting}
                    >
                        Publish
                    </LoadingButton>
                </>
            )}
        </Box>
    );

    const BreadcrumbsComponent = (): JSX.Element => (
        <Grid container justifyContent="center" sx={{ width: '100%', my: 2 }}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ display: 'flex', justifyContent: 'center' }}
            >
                {steps.map((step, index) => {
                    // Calculate the visible steps based on the index and currentStep
                    const isActivated = activatedSteps[index];
                    return (
                        <Grid size={{ xs: 'auto' }} key={index} sx={{ textAlign: 'center' }}>
                            {currentStep === step.label ? (
                                <Typography variant="subtitle2" color="text.primary">
                                    {step.label}
                                </Typography>
                            ) : (
                                <Link
                                    underline="hover"
                                    color={isActivated ? 'text.primary' : 'inherit'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentStep(step.label); // Set current step based on link click
                                        activateStep(index); // Mark the step as activated
                                    }}
                                >
                                    <Typography variant="body2">{step.label}</Typography>
                                </Link>
                            )}
                        </Grid>
                    );
                })}
            </Breadcrumbs>
        </Grid>
    );
    return { currentStep, setCanMoveFunc, BreadcrumbsComponent, MoveToStepComponent };
};
